import { Alert, Badge, Button, Card, CardContent, Spinner } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatBytes } from '@/features/shared/labels';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const STATUS_VARIANT = {
  ok: 'success',
  degraded: 'warning',
  down: 'destructive',
  up: 'success',
} as const;

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const health = useQuery({
    queryKey: queryKeys.admin.health,
    queryFn: () => api.admin.health(),
    refetchInterval: 30_000,
  });
  const data = health.data;
  return (
    <AdminPage
      section="dashboard"
      actions={
        <Button
          variant="outline"
          size="sm"
          loading={health.isFetching}
          onClick={() => void health.refetch()}
        >
          <RefreshCw aria-hidden /> {t('admin.refresh')}
        </Button>
      }
    >
      {health.isLoading && <Spinner label={t('app.loading')} />}
      {health.error && <Alert variant="destructive">{describeError(health.error)}</Alert>}
      {data && (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('admin.health.status')}</p>
                <p className="mt-1">
                  <Badge variant={STATUS_VARIANT[data.status]}>
                    {t(`admin.health.states.${data.status}`)}
                  </Badge>
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('admin.health.version')}</p>
                <p className="mt-1 font-mono text-sm">{data.version}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('admin.health.uptime')}</p>
                <p className="mt-1 text-sm">{Math.round(data.uptimeSeconds / 3600)} h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('admin.health.storage')}</p>
                <p className="mt-1 text-sm">
                  {data.storage.totalAssets} {t('admin.health.assets')} ·{' '}
                  {formatBytes(data.storage.totalBytes)}
                </p>
              </CardContent>
            </Card>
          </div>

          <section aria-labelledby="checks-title">
            <h2 id="checks-title" className="mb-2 text-lg font-semibold">
              {t('admin.health.checks')}
            </h2>
            <ResponsiveTable
              caption={t('admin.health.checks')}
              rowKey={(c) => c.name}
              rows={data.checks}
              columns={[
                {
                  key: 'name',
                  header: t('admin.health.check'),
                  cell: (c) => <span className="font-medium">{c.name}</span>,
                },
                {
                  key: 'status',
                  header: t('admin.health.status'),
                  cell: (c) => (
                    <Badge variant={STATUS_VARIANT[c.status]}>
                      {t(`admin.health.states.${c.status}`)}
                    </Badge>
                  ),
                },
                {
                  key: 'latency',
                  header: t('admin.health.latency'),
                  cell: (c) => (c.latencyMs != null ? `${c.latencyMs} ms` : '–'),
                },
                { key: 'detail', header: t('admin.health.detail'), cell: (c) => c.detail ?? '–' },
              ]}
            />
          </section>

          <section aria-labelledby="queues-title">
            <h2 id="queues-title" className="mb-2 text-lg font-semibold">
              {t('admin.health.queues')}
            </h2>
            <ResponsiveTable
              caption={t('admin.health.queues')}
              rowKey={(q) => q.name}
              rows={data.queues}
              columns={[
                {
                  key: 'name',
                  header: t('admin.jobs.queue'),
                  cell: (q) => (
                    <span className="font-mono text-xs">
                      {q.name}
                      {q.paused && (
                        <Badge variant="warning" className="ml-2">
                          {t('admin.health.paused')}
                        </Badge>
                      )}
                    </span>
                  ),
                },
                { key: 'waiting', header: t('admin.health.waiting'), cell: (q) => q.waiting },
                { key: 'active', header: t('admin.health.active'), cell: (q) => q.active },
                { key: 'completed', header: t('admin.health.completed'), cell: (q) => q.completed },
                {
                  key: 'failed',
                  header: t('admin.health.failed'),
                  cell: (q) => (q.failed > 0 ? <Badge variant="destructive">{q.failed}</Badge> : 0),
                },
                { key: 'delayed', header: t('admin.health.delayed'), cell: (q) => q.delayed },
              ]}
            />
          </section>
        </div>
      )}
    </AdminPage>
  );
}
